/**
 * Implements the client-side logic for the Firebolt data layer and a unified model factory pattern for client-side Firestore data access.
 *
 * @see {@link FireCacheManager} - Integrates deeply with the `FireCacheManager` for instance caching and memory management.
 * @see {@link https://github.com/preactjs/signals/blob/main/packages/react/README.md} - The reactive query system is built upon Preact Signals.
 * @see {@link https://github.com/luisherranz/deepsignal} - Uses `deepsignal` to make arrays within query results reactive.
 *
 * This file is the engine of the client-side data layer. Its primary export is `createClientQuery`, a function that
 * creates a live, reactive, and efficient subscription to a Firestore collection.
 *
 * The key components are:
 * 1.  `createClientDataConverter`: A stateful `FirestoreDataConverter` that is the bridge between raw Firestore data and reactive application objects. It uses the `FireCacheManager` to ensure that each document is represented by a single, unique object instance.
 * 2.  `createClientInstance`: A factory function that takes raw data and transforms it into a reactive object using `@preact-signals/utils`'s `createFlatStore`. It also makes array properties deeply reactive using `deepsignal`.
 * 3.  `createClientQuery`: The main entry point. It takes a `ModelSpec` and context, sets up a Firestore `onSnapshot` listener, and manages the lifecycle of the resulting data. It uses a `WeakRef` cache to ensure that identical queries share the same listener.
 * 4.  `applyCollectionDiff`: A utility that efficiently updates the local array of results with new data from Firestore, correctly releasing instances that are no longer part of the query.
 *
 * This setup provides a highly optimized, real-time data layer that is both easy to use and robust against common issues like memory leaks and state inconsistency.
 *
 * This module provides a `createClientModel` function that creates type-safe, reactive model classes
 * from ModelSpec definitions. It combines the reactive query system with custom methods and
 * computed properties to provide a clean, consistent API for data access.
 *
 * This is part of the effort to unify data access patterns across the codebase. It replaces
 * the ad-hoc patterns of using `createClientQuery` directly and provides a more intuitive,
 * object-oriented interface while maintaining the benefits of the reactive signal-based system.
 */

import type { DeepSignal } from 'deepsignal/react';
import type { Simplify } from 'type-fest';

import type { FireSnapshot } from '@elara/firebolt/FireCacheManager';
import type { AbstractBaseInstance, AnyModelSpec } from '@elara/firebolt/shared';

import type { ClientModelInstance, MakeExtensions, ModelExtensions } from './types.js';

import { batch } from '@preact/signals-core';
import { createFlatStore } from '@preact-signals/utils';
import { deepSignal } from 'deepsignal/react';
import { isEqual, pick } from 'lodash-es';

import { objectKey } from '@elara/core/introspection';

import { valueToFirestore } from './utils.js';

/**
 * @internal
 * @function createClientInstance
 * Factory function used by the `FireCacheManager` to create a new reactive model instance.
 * It takes a snapshot, creates a reactive store using `createFlatStore`, and makes array properties
 * deeply reactive using `deepsignal`.
 * @param snapshot - The Firestore snapshot.
 * @param data - The data from the snapshot, transformed into the model's input type.
 * @param makeExtensions - A function that creates the extensions for the model instance, allowing for custom methods and computed properties.
 * @returns A new, fully reactive model instance.
 */
export function createClientInstance<
  Snap extends FireSnapshot<Spec['DataType']>,
  Spec extends AnyModelSpec,
  Extensions extends ModelExtensions<Spec>,
>(snapshot: Snap, data: Spec['DataType'], makeExtensions: MakeExtensions<Spec, Extensions>) {
  const path = snapshot.ref.path;
  const base: AbstractBaseInstance<Spec['DataType']> = {
    id: snapshot.id,
    path,
    ref: { id: snapshot.id, path },
    _hydrate(data: Partial<Spec['DataType']>): void {
      const updates: Partial<Spec['DataType']> = {};
      let hasChanges = false;
      for (const key in data) {
        const newValue = data[key];
        const currentValue = (this as any)[key];
        const actualCurrentValue = currentValue?.peek ? currentValue.peek() : currentValue;
        if (!isEqual(actualCurrentValue, newValue)) {
          updates[key] = newValue;
          hasChanges = true;
        }
      }
      if (hasChanges) {
        // Batch signal updates for performance
        batch(() => {
          updateInstance(updates);
          if (objectKey('onHydrated').isMethodIn(this, 0)) {
            this.onHydrated();
          }
        });
      }
    },
    toFirestore(): Spec['DataType'] {
      const recoveredData = pick(this, Object.keys(data));
      return valueToFirestore(recoveredData) as Spec['DataType'];
    },
  };
  const extensions = makeExtensions();
  const instanceData = mergeByDescriptors(base, data, extensions);
  const [reactiveInstance, updateInstanceRaw] = createFlatStore(instanceData);
  const updateInstance = updateInstanceRaw as (updates: Partial<Spec['DataType']>) => void;
  return reactiveInstance satisfies ClientModelInstance<Spec, Extensions>;
}

function mergeByDescriptors<Base extends object, Data extends object, Extensions extends object>(
  base: Base,
  data: Data,
  extensions: Extensions,
): Simplify<Base & Data & Extensions> {
  const result = Object.create(base) as Base & Data & Extensions;
  for (const source of [base, data, extensions]) {
    const sourceDescriptors = Object.getOwnPropertyDescriptors(source);
    for (const propKey in sourceDescriptors) {
      const descriptor = { ...sourceDescriptors[propKey] };
      if (!descriptor.get && !descriptor.set && typeof descriptor.value !== 'function') {
        descriptor.value = prepareValueForReactivity(descriptor.value);
      }
      Object.defineProperty(result, propKey, descriptor);
    }
  }
  return result;
}

/**
 * @internal
 * @function prepareValueForReactivity
 * A utility to wrap array values in `deepsignal` to make them deeply reactive.
 * Other values are returned as-is.
 */
function prepareValueForReactivity<T>(value: T[]): DeepSignal<T[]>;
function prepareValueForReactivity(value: null | undefined): null;
function prepareValueForReactivity<T>(value: T): T;

function prepareValueForReactivity<T>(value: T): DeepSignal<T> | T | null {
  if (value === null || value === undefined) return null;
  try {
    if (Array.isArray(value)) return deepSignal(value);
  } catch (error) {
    console.error(`Error preparing value for reactivity: ${String(error)}`, { error, value });
  }
  return value;
}
